import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../../revision-system/context/RevisionContext';
import { Card, SessionStatistics, Rating } from '../types/models';
import { SpacedRepetitionService } from '../services/SpacedRepetitionService';
import './RevisionSession.css';

/**
 * Revision Session component
 * Conducts spaced repetition study sessions
 * 
 * Features:
 * - Loads due cards on session start
 * - Manages current card and remaining cards queue
 * - Tracks show/hide answer state
 * - Records session start time and review times
 */
const RevisionSession: React.FC = () => {
  const navigate = useNavigate();
  const { sectionId } = useParams<{ sectionId: string }>();
  const [searchParams] = useSearchParams();
  const reviewAllMode = searchParams.get('mode') === 'all';
  const { sections, isLoading, user, config, firestoreService } = useAppContext();

  // Direction picker
  const [direction, setDirection] = useState<'front-to-back' | 'back-to-front' | null>(null);

  // Setup / filter state
  const [allDueCards, setAllDueCards] = useState<Card[]>([]);
  const [sessionStarted, setSessionStarted] = useState<boolean>(false);
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(new Set());
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

  // Session state
  const [currentCard, setCurrentCard] = useState<Card | null>(null);
  const [remainingCards, setRemainingCards] = useState<Card[]>([]);
  const [againQueue, setAgainQueue] = useState<Card[]>([]);
  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [cardStartTime, setCardStartTime] = useState<Date | null>(null);
  const [sessionStats, setSessionStats] = useState<SessionStatistics>({
    cardsReviewed: 0,
    duration: 0,
    ratings: { again: 0, hard: 0, good: 0, easy: 0, tomorrow: 0 },
    averageTimePerCard: 0,
  });
  const [isLoadingCards, setIsLoadingCards] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Services
  const spacedRepetitionService = new SpacedRepetitionService();

  /**
   * Load cards once direction is picked (populates the filter screen)
   * In 'review all' mode, loads ALL cards from the section regardless of schedule.
   * In normal mode, loads only due cards.
   */
  useEffect(() => {
    if (!direction) return;
    const loadCards = async () => {
      try {
        setIsLoadingCards(true);
        setError(null);

        let cards: Card[];

        if (reviewAllMode && sectionId) {
          // Review All mode: get every card in the section
          cards = await firestoreService.getCardsBySection(sectionId);
        } else {
          // Normal mode: get only due cards
          const now = new Date();
          cards = await firestoreService.getDueCards(now);

          // If a section is fixed via URL, restrict to it
          if (sectionId) {
            cards = cards.filter(card => card.sectionId === sectionId);
          }
        }

        setAllDueCards(cards);

        // Default: all available sections selected
        const availableSectionIds = new Set(cards.map(c => c.sectionId));
        setSelectedSectionIds(availableSectionIds);
        setSelectedTags(new Set()); // no tag filter = include all
      } catch (err) {
        console.error('Error loading cards:', err);
        setError('Failed to load cards. Please try again.');
      } finally {
        setIsLoadingCards(false);
      }
    };

    loadCards();
  }, [sectionId, direction, reviewAllMode]);

  /**
   * Build the filtered card set and start the session
   */
  const beginSession = () => {
    let cards = allDueCards.filter(c => selectedSectionIds.has(c.sectionId));

    // Tag filter: if any tags selected, card must have at least one of them
    if (selectedTags.size > 0) {
      cards = cards.filter(c => (c.tags || []).some(t => selectedTags.has(t)));
    }

    if (cards.length === 0) {
      setError('No cards match your selected filters');
      return;
    }

    setError(null);
    setSessionStartTime(new Date());
    setCardStartTime(new Date());
    setCurrentCard(cards[0]);
    setRemainingCards(cards.slice(1));
    setShowAnswer(false);
    setSessionStarted(true);
  };

  /**
   * Handle revealing the answer
   */
  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  /**
   * Handle rating a card and moving to the next one
   */
  const handleRating = async (rating: Rating) => {
    if (!currentCard || !cardStartTime || !sessionStartTime) return;

    try {
      // Calculate time spent on this card
      const now = new Date();
      const timeSpent = now.getTime() - cardStartTime.getTime();

      // Calculate next review schedule
      const scheduleResult = spacedRepetitionService.calculateNextReview(
        currentCard,
        rating,
        now
      );

      // Update card schedule in Firestore
      await firestoreService.updateCard(currentCard.id, {
        schedule: {
          nextReviewDate: scheduleResult.nextReviewDate,
          interval: scheduleResult.interval,
          easeFactor: scheduleResult.easeFactor,
          repetitions: scheduleResult.repetitions,
          lastReviewDate: now,
        },
      });

      // Record review in Firestore
      await firestoreService.recordReview({
        cardId: currentCard.id,
        sectionId: currentCard.sectionId,
        rating,
        reviewDate: now,
        timeSpent,
        previousInterval: currentCard.schedule.interval,
        newInterval: scheduleResult.interval,
      });

      // Update session statistics
      const updatedStats = {
        ...sessionStats,
        cardsReviewed: sessionStats.cardsReviewed + 1,
        ratings: {
          ...sessionStats.ratings,
          [rating]: sessionStats.ratings[rating] + 1,
        },
      };
      setSessionStats(updatedStats);

      // Move to next card or end session
      if (rating === 'again') {
        // Re-queue at end of current remaining cards
        const newAgainQueue = [...againQueue, currentCard];
        if (remainingCards.length > 0) {
          setCurrentCard(remainingCards[0]);
          setRemainingCards([...remainingCards.slice(1), ...newAgainQueue]);
          setAgainQueue([]);
        } else if (newAgainQueue.length > 0) {
          setCurrentCard(newAgainQueue[0]);
          setRemainingCards(newAgainQueue.slice(1));
          setAgainQueue([]);
        } else {
          setCurrentCard(null);
        }
      } else if (remainingCards.length > 0) {
        setCurrentCard(remainingCards[0]);
        setRemainingCards(remainingCards.slice(1));
      } else if (againQueue.length > 0) {
        setCurrentCard(againQueue[0]);
        setRemainingCards(againQueue.slice(1));
        setAgainQueue([]);
      } else {
        // Session complete
        const sessionDuration = now.getTime() - sessionStartTime.getTime();
        const finalStats = {
          ...updatedStats,
          duration: sessionDuration,
          averageTimePerCard: sessionDuration / updatedStats.cardsReviewed,
        };
        setSessionStats(finalStats);
        setCurrentCard(null);
      }
      setShowAnswer(false);
      setCardStartTime(new Date());
    } catch (err) {
      console.error('Error processing rating:', err);
      setError('Failed to save review. Please try again.');
    }
  };

  /**
   * Handle exiting the session
   */
  const handleExit = () => {
    navigate(config.routing.basePath);
  };

  // Direction picker screen
  if (!direction) {
    return (
      <div className="revision-session">
        <div className="direction-picker">
          <h2>How would you like to review?</h2>
          <div className="direction-options">
            <button
              className="direction-btn"
              onClick={() => setDirection('front-to-back')}
            >
              <span className="direction-icon">→</span>
              <span className="direction-title">Front → Back</span>
              <span className="direction-desc">See the question, recall the answer</span>
            </button>
            <button
              className="direction-btn"
              onClick={() => setDirection('back-to-front')}
            >
              <span className="direction-icon">←</span>
              <span className="direction-title">Back → Front</span>
              <span className="direction-desc">See the answer, recall the question</span>
            </button>
          </div>
          <button className="btn btn-secondary" onClick={handleExit}>Cancel</button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading || isLoadingCards) {
    return (
      <div className="revision-session">
        <div className="loading">Loading session...</div>
      </div>
    );
  }

  // Filter / setup screen — shown after direction is picked, before session starts
  if (!sessionStarted) {
    // Sections that actually have due cards
    const dueSectionIds = Array.from(new Set(allDueCards.map(c => c.sectionId)));
    const dueSections = sections.filter(s => dueSectionIds.includes(s.id));

    // All tags present across due cards
    const availableTags = Array.from(
      new Set(allDueCards.flatMap(c => c.tags || []))
    ).sort();

    // Count how many cards the current filter would include
    let matchCount = allDueCards.filter(c => selectedSectionIds.has(c.sectionId));
    if (selectedTags.size > 0) {
      matchCount = matchCount.filter(c => (c.tags || []).some(t => selectedTags.has(t)));
    }

    const toggleSection = (id: string) => {
      setSelectedSectionIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    };

    const toggleTag = (tag: string) => {
      setSelectedTags(prev => {
        const next = new Set(prev);
        next.has(tag) ? next.delete(tag) : next.add(tag);
        return next;
      });
    };

    if (allDueCards.length === 0) {
      return (
        <div className="revision-session">
          <div className="error-message">
            <h2>{reviewAllMode ? 'No cards in this section' : 'No cards due for review'}</h2>
            <button className="btn btn-primary" onClick={handleExit}>Return to Dashboard</button>
          </div>
        </div>
      );
    }

    return (
      <div className="revision-session">
        <div className="session-setup">
          <h2>Customise your session</h2>

          {!sectionId && (
            <div className="setup-group">
              <div className="setup-group-header">
                <h3>Sections</h3>
                <div className="setup-bulk">
                  <button type="button" onClick={() => setSelectedSectionIds(new Set(dueSectionIds))}>All</button>
                  <button type="button" onClick={() => setSelectedSectionIds(new Set())}>None</button>
                </div>
              </div>
              <div className="setup-options">
                {dueSections.map(s => {
                  const count = allDueCards.filter(c => c.sectionId === s.id).length;
                  return (
                    <label key={s.id} className={`setup-chip ${selectedSectionIds.has(s.id) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedSectionIds.has(s.id)}
                        onChange={() => toggleSection(s.id)}
                      />
                      {s.name} <span className="setup-chip-count">({count})</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {availableTags.length > 0 && (
            <div className="setup-group">
              <div className="setup-group-header">
                <h3>Tags</h3>
                <div className="setup-bulk">
                  <button type="button" onClick={() => setSelectedTags(new Set())}>Clear</button>
                </div>
              </div>
              <p className="setup-hint">
                {selectedTags.size === 0
                  ? 'No tag filter — all cards included.'
                  : 'Showing cards with any of the selected tags.'}
              </p>
              <div className="setup-options">
                {availableTags.map(tag => (
                  <label key={tag} className={`setup-chip ${selectedTags.has(tag) ? 'selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedTags.has(tag)}
                      onChange={() => toggleTag(tag)}
                    />
                    {tag}
                  </label>
                ))}
              </div>
            </div>
          )}

          {error && <div className="error-banner">{error}</div>}

          <div className="setup-actions">
            <button className="btn btn-secondary" onClick={handleExit}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={beginSession}
              disabled={matchCount.length === 0}
            >
              Start Session ({matchCount.length} {matchCount.length === 1 ? 'card' : 'cards'})
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !currentCard) {
    return (
      <div className="revision-session">
        <div className="error-message">
          <h2>{error}</h2>
          <button className="btn btn-primary" onClick={handleExit}>
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Session complete state
  if (!currentCard && sessionStats.cardsReviewed > 0) {
    return (
      <div className="revision-session">
        <div className="session-complete">
          <h2>Session Complete!</h2>
          <div className="session-stats">
            <p>Cards Reviewed: {sessionStats.cardsReviewed}</p>
            <p>Duration: {Math.round(sessionStats.duration / 1000)}s</p>
            <p>Average Time per Card: {Math.round(sessionStats.averageTimePerCard / 1000)}s</p>
            <div className="rating-distribution">
              <h3>Rating Distribution:</h3>
              <p>Again: {sessionStats.ratings.again}</p>
              <p>Hard: {sessionStats.ratings.hard}</p>
              <p>Good: {sessionStats.ratings.good}</p>
              <p>Easy: {sessionStats.ratings.easy}</p>
            </div>
          </div>
          <button className="btn btn-primary" onClick={handleExit}>
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const section = sectionId ? sections.find(s => s.id === sectionId) : null;
  const currentCardSection = currentCard ? sections.find(s => s.id === currentCard.sectionId) : null;

  // Preview intervals for current card
  const intervals = currentCard ? spacedRepetitionService.previewIntervals(currentCard) : null;

  const formatInterval = (days: number) => {
    if (days === 0) return 'Now';
    if (days === 1) return '1d';
    if (days < 30) return `${days}d`;
    if (days < 365) return `${Math.round(days / 30)}mo`;
    return `${Math.round(days / 365)}y`;
  };

  return (
    <div className="revision-session">
      <header className="revision-session-header">
        <h1>Revision Session</h1>
        <div className="session-info">
          {section ? (
            <span>Reviewing: {section.name}</span>
          ) : (
            <span>All Sections</span>
          )}
          <span className="cards-remaining">
            {remainingCards.length + 1} cards remaining
          </span>
        </div>
        <button 
          className="btn btn-secondary"
          onClick={handleExit}
        >
          Exit Session
        </button>
      </header>

      <div className="revision-session-content">
        {currentCard && (
          <div className="card-display">
            {!sectionId && currentCardSection && (
              <div className="card-section-label">{currentCardSection.name}</div>
            )}
            <div className="card-front">
              <h2>{direction === 'back-to-front' ? 'Answer' : 'Question'}</h2>
              <div 
                className="card-text"
                dangerouslySetInnerHTML={{ __html: direction === 'back-to-front' ? currentCard.back.html : currentCard.front.html }}
              />
            </div>

            {showAnswer && (
              <div className="card-back">
                <h2>{direction === 'back-to-front' ? 'Question' : 'Answer'}</h2>
                <div 
                  className="card-text"
                  dangerouslySetInnerHTML={{ __html: direction === 'back-to-front' ? currentCard.front.html : currentCard.back.html }}
                />
              </div>
            )}

            <div className="card-actions">
              {!showAnswer ? (
                <button 
                  className="btn btn-primary btn-large"
                  onClick={handleShowAnswer}
                >
                  Show Answer
                </button>
              ) : (
                <div className="rating-buttons">
                  <button 
                    className="btn btn-rating btn-again"
                    onClick={() => handleRating('again')}
                  >
                    Again<span className="interval-label">{intervals ? formatInterval(intervals.again) : ''}</span>
                  </button>
                  <button
                    className="btn btn-rating btn-tomorrow"
                    onClick={() => handleRating('tomorrow')}
                  >
                    Tomorrow<span className="interval-label">1d</span>
                  </button>
                  <button 
                    className="btn btn-rating btn-hard"
                    onClick={() => handleRating('hard')}
                  >
                    Hard<span className="interval-label">{intervals ? formatInterval(intervals.hard) : ''}</span>
                  </button>
                  <button 
                    className="btn btn-rating btn-good"
                    onClick={() => handleRating('good')}
                  >
                    Good<span className="interval-label">{intervals ? formatInterval(intervals.good) : ''}</span>
                  </button>
                  <button 
                    className="btn btn-rating btn-easy"
                    onClick={() => handleRating('easy')}
                  >
                    Easy<span className="interval-label">{intervals ? formatInterval(intervals.easy) : ''}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="error-banner">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default RevisionSession;
